// Local A/B Test Framework
// Client-side A/B tests using localStorage
// For server-side feature flags, use Unleash instead

import * as Sentry from '@sentry/vue'
import STORAGE_KEYS from '@/constant/storageKeys'
import Config from '@/config'

const LOCAL_VARIANTS = {
  CONTROL: 'control',
  TREATMENT: 'treatment',
}

const LOCAL_AB_TESTS = {
  UI_VARIANT: {
    name: 'uiVariant',
    storageKey: STORAGE_KEYS.UI_VARIANT_PERCENTAGE,
    rolloutWeight: Config.uiVariantRolloutWeight, // Read from environment variable (0-100)
    queryParam: 'uiVariantPercentage', // ?uiVariantPercentage=20 (0-100 scale), use -1 to force treatment for testing
    mixpanelProp: 'uiVariant',
  },
  NEW_UI_ONBOARDING: {
    name: 'newUIOnboarding',
    storageKey: STORAGE_KEYS.UI_VARIANT_PERCENTAGE,
    rolloutWeight: Config.uiOnboardingRolloutWeight, // 0 = all control, 100 = all treatment, 0-100 = percentage
    queryParam: 'uiVariantPercentage', // ?newUIOnboardingPercentage=20 (0-100 scale), use -1 to force treatment for testing
  },
}

/**
 * Get assignment value override from URL query parameter
 * @param {string} queryParam - The query param name
 * @returns {number|null} Assignment value (-1 to 100) or null
 *
 * Special values for testing:
 * -1: Force treatment (works even when rolloutWeight = 0)
 * 100: Force control when rolloutWeight < 100
 */
const getAssignmentValueFromQuery = (queryParam) => {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    const value = urlParams.get(queryParam)

    if (value === null) {
      return null
    }

    const numValue = parseFloat(value)

    if (!Number.isNaN(numValue) && numValue >= -1 && numValue <= 100) {
      return numValue
    }

    return null
  } catch (error) {
    return null
  }
}

/**
 * Generate assignment value for user (0-100 scale)
 * This value is immutable once assigned
 * @returns {number} Random number between 0 and 100
 */
const generateAssignmentValue = () => {
  return Math.floor(Math.random() * 100)
}

/**
 * Compute variant based on assignment value and rollout weight (0-100 scale)
 * Uses <= operator to include edge case where assignmentValue = rolloutWeight
 * @param {number} assignmentValue - User's assignment value (-1 to 100, negative for testing force)
 * @param {number} rolloutWeight - Current rollout weight (0-100)
 * @returns {string} Computed variant
 */
const computeVariant = (assignmentValue, rolloutWeight) => {
  return assignmentValue < rolloutWeight ? LOCAL_VARIANTS.TREATMENT : LOCAL_VARIANTS.CONTROL
}

/**
 * Get the current variant for a specific local A/B test
 *
 * Priority order:
 * 1. Query param override (-1 to 100 number, -1 forces treatment for testing)
 * 2. Stored assignment value from localStorage (-1 to 100 number)
 * 3. Generate new assignment value (0-100 number)
 *
 * @param {string} testKey - Key from LOCAL_AB_TESTS (e.g., 'UI_VARIANT')
 * @returns {string} The variant ('control' or 'treatment')
 */
const getLocalVariant = (testKey) => {
  const config = LOCAL_AB_TESTS[testKey]

  if (!config) {
    Sentry.captureMessage(`Local A/B test config not found: ${testKey}`)
    return LOCAL_VARIANTS.CONTROL
  }

  try {
    let assignmentValue

    // Priority 1: Query param override
    const queryValue = getAssignmentValueFromQuery(config.queryParam)

    if (queryValue !== null) {
      // Save testing query override to localStorage (same format as normal assignment)
      localStorage.setItem(config.storageKey, queryValue.toString())
      assignmentValue = queryValue
    } else {
      // Priority 2: Get from localStorage
      const storedValue = localStorage.getItem(config.storageKey)

      if (storedValue) {
        const parsedValue = parseFloat(storedValue)

        // Validate stored value (-1 to 100 scale)
        // Negative values allowed from testing query override
        if (!Number.isNaN(parsedValue) && parsedValue >= -1 && parsedValue <= 100) {
          assignmentValue = parsedValue
        }
      }

      // Priority 3: Generate new assignment value if not found
      if (assignmentValue === undefined) {
        assignmentValue = generateAssignmentValue()
        localStorage.setItem(config.storageKey, assignmentValue.toString())
      }
    }

    // Compute variant based on assignment value and rollout weight
    return computeVariant(assignmentValue, config.rolloutWeight)
  } catch (error) {
    // localStorage might not be available (private browsing, etc.)
    Sentry.captureException(error, {
      extra: {
        context: 'getLocalVariant',
        testKey,
        message: 'Failed to access localStorage for local A/B test',
      },
    })
    return LOCAL_VARIANTS.CONTROL
  }
}

/**
 * Clear the variant assignment for a specific local A/B test
 * @param {string} testKey - Key from LOCAL_AB_TESTS
 */
const clearLocalVariant = (testKey) => {
  const config = LOCAL_AB_TESTS[testKey]

  if (!config) {
    return
  }

  try {
    localStorage.removeItem(config.storageKey)
  } catch (error) {
    // Silent fail if localStorage is not available
  }
}

export {
  getLocalVariant,
  clearLocalVariant,
  getAssignmentValueFromQuery,
  generateAssignmentValue,
  computeVariant,
  LOCAL_AB_TESTS,
  LOCAL_VARIANTS,
}
